// AppLock React context — owns the AppLock instance lifecycle.
// Uses ui: false per INTEGRATION.md; React renders its own lock/setup/welcome screens.

import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { AppLock, type AppLockInstance } from '@ascend/applock';
import { Loader2 } from 'lucide-react';
import { ensureDBSchema, setIDBAdapter, resetIDBAdapter } from '../../persist/idb';

const DEFERRED_KEY = 'carry-roll-app-lock-deferred';

export type LockDecision = 'pending' | 'skipped' | 'enabled';

const APPLOCK_META_KEYS = ['applock_hash', 'applock_salt', 'applock_config', 'applock_version'];

function clearAppLockMetaKeys() {
  for (const key of APPLOCK_META_KEYS) {
    localStorage.removeItem(key);
  }
}

function getStoredDecision(): LockDecision {
  const stored = localStorage.getItem(DEFERRED_KEY);
  if (stored === 'skipped') return 'skipped';
  if (stored === 'enabled') return 'enabled';
  return 'pending';
}

async function createLock(): Promise<AppLockInstance> {
  // Ensure the DB schema exists at the correct version before AppLock opens it.
  await ensureDBSchema();

  return AppLock.init({
    mode: 'encrypted',
    storageBackend: 'indexedDB',
    idbDatabaseName: 'carry-roll-db',
    appRootSelector: '#root',
    lockOnHide: true,
    idleTimeout: 300,
    ui: false, // ALWAYS false — React renders its own screens
  });
}

interface LockState {
  lock: AppLockInstance | null;
  isLocked: boolean;
  isUnlocked: boolean;
  isConfigured: boolean;
  decision: LockDecision;
}

interface AppLockContextValue extends LockState {
  isEnabled: boolean;
  skipEncryption: () => void;
  enableEncryption: () => void;
}

const AppLockContext = createContext<AppLockContextValue | null>(null);

function getInitialState(): LockState {
  const decision = getStoredDecision();
  return {
    lock: null,
    isLocked: false,
    isUnlocked: decision === 'skipped',
    isConfigured: false,
    decision,
  };
}

export function AppLockProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LockState>(getInitialState);
  const instanceRef = useRef<AppLockInstance | null>(null);

  const skipEncryption = useCallback(() => {
    localStorage.setItem(DEFERRED_KEY, 'skipped');
    clearAppLockMetaKeys();
    resetIDBAdapter();
    if (instanceRef.current) {
      instanceRef.current.destroy();
      instanceRef.current = null;
    }
    setState({
      lock: null,
      isLocked: false,
      isUnlocked: true,
      isConfigured: false,
      decision: 'skipped',
    });
  }, []);

  const enableEncryption = useCallback(() => {
    localStorage.setItem(DEFERRED_KEY, 'enabled');
    clearAppLockMetaKeys();
    setState((prev) => ({
      ...prev,
      decision: 'enabled',
      isUnlocked: false,
      isConfigured: false,
    }));
  }, []);

  useEffect(() => {
    if (state.decision !== 'enabled') return;

    let destroyed = false;

    (async () => {
      const instance = await createLock();
      if (destroyed) {
        instance.destroy();
        return;
      }
      instanceRef.current = instance;

      const onLock = () => {
        resetIDBAdapter();
        setState((prev) => ({ ...prev, isLocked: true, isUnlocked: false }));
      };
      const onUnlock = () => {
        if (instance.idb) setIDBAdapter(instance.idb);
        setState((prev) => ({
          ...prev,
          isLocked: false,
          isUnlocked: true,
          isConfigured: true,
        }));
      };
      const onPasswordClear = () => {
        resetIDBAdapter();
        setState((prev) => ({ ...prev, isConfigured: false }));
      };
      const onSetup = () => {
        if (instance.idb) setIDBAdapter(instance.idb);
        setState((prev) => ({ ...prev, isConfigured: true }));
      };

      instance.on('lock', onLock);
      instance.on('unlock', onUnlock);
      instance.on('password:clear', onPasswordClear);
      instance.on('setup', onSetup);

      // If already unlocked (returning user with valid session), wire the adapter.
      if (instance.state === 'unlocked' && instance.idb) {
        setIDBAdapter(instance.idb);
      }

      setState((prev) => ({
        ...prev,
        lock: instance,
        isLocked: instance.isLocked,
        isUnlocked:
          instance.state === 'unlocked' || instance.state === 'uninitialized',
        isConfigured: instance.isConfigured,
      }));
    })();

    return () => {
      destroyed = true;
      resetIDBAdapter();
      if (instanceRef.current) {
        instanceRef.current.destroy();
        instanceRef.current = null;
      }
    };
  }, [state.decision]);

  // Loading state while lock instance is being created
  if (state.decision === 'enabled' && !state.lock) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <Loader2
          size={32}
          className="animate-spin"
          style={{ color: 'var(--color-accent)' }}
          aria-label="Loading"
        />
      </div>
    );
  }

  return (
    <AppLockContext.Provider
      value={{
        ...state,
        isEnabled: state.decision === 'enabled' && state.lock !== null,
        skipEncryption,
        enableEncryption,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
}

export function useAppLock(): AppLockContextValue {
  const ctx = useContext(AppLockContext);
  if (!ctx) throw new Error('useAppLock must be used within AppLockProvider');
  return ctx;
}
